import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/layout/SiteChrome";
import { sellStore, useSell, type Answers } from "../store/sellStore";
import { fetchAlgorithm, submitLead } from "../services/api";

type Q = { key: string; title: string; subtitle: string; icon: string; options: { value: string; label: string; desc: string; factor: number; emoji: string; questionId?: string; optionIndex?: number; type?: string; priceValue?: number; }[]; questionId?: string };

const DEFAULT_QUESTIONS: Q[] = [
  { key:"screen",  title:"How is your screen?",  subtitle:"Examine closely under bright light", icon:"🖥",
    options:[{ value:"flawless",label:"Flawless",       desc:"No scratches, perfect condition",factor:1,   emoji:"✨"},{ value:"minor",label:"Minor Scratches",  desc:"Light marks, no cracks",        factor:0.85,emoji:"🔍"},{ value:"cracked",label:"Cracked/Shattered",desc:"Visible cracks or breaks",       factor:0.55,emoji:"💔"}]},
  { key:"battery", title:"Battery Health",        subtitle:"Check Settings → Battery Health",  icon:"🔋",
    options:[{ value:"high",  label:"Above 90%",        desc:"Excellent battery life",          factor:1,   emoji:"💚"},{ value:"mid",  label:"80% – 90%",         desc:"Good battery performance",       factor:0.92,emoji:"💛"},{ value:"low",  label:"Below 80%",          desc:"Reduced battery life",           factor:0.78,emoji:"🔴"}]},
  { key:"physical",title:"Physical Condition",    subtitle:"Check body, frame and back panel",  icon:"📦",
    options:[{ value:"new",   label:"Like New",          desc:"No dents, scratches or marks",   factor:1,   emoji:"🏆"},{ value:"dents",label:"Minor Dents",       desc:"Small marks, still looks good",  factor:0.88,emoji:"👍"},{ value:"major",label:"Major Damage",      desc:"Visible cracks or deep dents",   factor:0.6, emoji:"⚠️"}]},
  { key:"accessories",title:"Accessories Included",subtitle:"What will you include?",           icon:"🎁",
    options:[{ value:"all",   label:"Complete Box",      desc:"Box, charger, cable & all",      factor:1,   emoji:"📦"},{ value:"charger",label:"Charger Only",    desc:"Charger and cable",              factor:0.95,emoji:"🔌"},{ value:"none", label:"Device Only",        desc:"No accessories",                 factor:0.9, emoji:"📱"}]},
  { key:"functional",title:"Functional Issues?",  subtitle:"Test all hardware before selecting",icon:"⚙️",
    options:[{ value:"ok",    label:"Everything Works",  desc:"Camera, speakers, buttons fine", factor:1,   emoji:"✅"},{ value:"minor",label:"Minor Issues",      desc:"Some features have small problems",factor:0.85,emoji:"⚡"},{ value:"major",label:"Major Issues",       desc:"Device has significant problems", factor:0.65,emoji:"🔧"}]},
];

// Map algo questions to our local question format
function mapAlgoToQuestions(algo: any, localQuestions: Q[]): Q[] {
  if (!algo?.questions?.length) return localQuestions;
  return algo.questions.filter((q: any) => q.active).map((q: any, qi: number): Q => {
    // Match to a local question key by label similarity
    const keyMap: Record<string, string> = {
      screen: "screen", battery: "battery", physical: "physical", accessories: "accessories", functional: "functional",
    };
    const matchedKey = Object.keys(keyMap).find(k => q.label.toLowerCase().includes(k));
    const questionId = q._id || q.id || `q_${qi}`;
    const fallbackKey = questionId || localQuestions[qi % localQuestions.length]?.key || `q_${qi}`;
    return {
      key: matchedKey || fallbackKey,
      title: q.label,
      subtitle: localQuestions[qi % localQuestions.length]?.subtitle || "Select the best option",
      icon: localQuestions[qi % localQuestions.length]?.icon || "❓",
      questionId: questionId,
      options: q.options.map((opt: any, oi: number) => {
        const val = Number(opt.value) || 0;
        return {
          value: `opt_${qi}_${oi}`,
          label: opt.label,
          desc: val === 0 ? "No price change" : opt.type === "percent" ? `${val > 0 ? "+" : ""}${val}% adjustment` : `₹${Math.abs(val).toLocaleString("en-IN")} ${val > 0 ? "bonus" : "deduction"}`,
          factor: opt.type === "percent" ? 1 + val / 100 : 1,
          emoji: oi === 0 ? "✨" : oi === 1 ? "🔍" : "💔",
          questionId: questionId,
          optionIndex: oi,
          type: opt.type || "flat",
          priceValue: val,
        };
      }),
    };
  });
}

export default function QuestionnairePage() {
  const sell = useSell();
  const nav  = useNavigate();
  const [step, setStep]   = useState(0);
  const [anim, setAnim]   = useState(false);
  const [questions, setQuestions] = useState<Q[]>(DEFAULT_QUESTIONS);
  const [loading, setLoading]     = useState(false);

  useEffect(() => { if (!sell.verified || !sell.brandId || !sell.modelId) nav("/"); }, [sell.verified, sell.brandId, sell.modelId, nav]);

  // Load algorithm from backend if we have algoId
  useEffect(() => {
    if (!sell.algoId) { setQuestions(DEFAULT_QUESTIONS); return; }
    setLoading(true);
    fetchAlgorithm(sell.algoId)
      .then(d => { const mapped = mapAlgoToQuestions(d.algorithm, DEFAULT_QUESTIONS); setQuestions(mapped.length ? mapped : DEFAULT_QUESTIONS); })
      .catch(() => setQuestions(DEFAULT_QUESTIONS))
      .finally(() => setLoading(false));
  }, [sell.algoId]);

  if (!sell.brandId || !sell.modelId || !sell.product) return null;
  const model = sell.product;

  const q        = questions[step];
  const progress = ((step + 1) / questions.length) * 100;
  const selected = sell.answers[q?.key] || [];

  const goNext = () => {
    if (anim || !q) return;
    if (step === questions.length - 1) {
      // Build human-readable condition report for technician
      sellStore.setConditionSummary(
        questions.map(qq => {
          const v = sell.answers[qq.key] || [];
          const selectedLabels = v.map(val => {
            const opt = qq.options.find(o => o.value === val);
            return opt?.label || val;
          }).filter(Boolean);
          return { question: qq.title, answer: selectedLabels.join(', ') || '' };
        }).filter(r => r.answer)
      );
      // Calculate final price
      let price: number = 0;
      if (sell.algoId && (sell.algoBasePrice || model?.basePrice)) {
        // Use backend algorithm additive logic
        const basePrice = Number(sell.algoBasePrice || model?.basePrice || 0);
        let calculatedPrice = basePrice;
        questions.forEach(qq => {
          const qId = qq.questionId;
          if (qId) {
            const ansIndices = sell.answerIndices[qId] || [];
            ansIndices.forEach(ansIdx => {
              const opt = qq.options[ansIdx];
              if (opt) {
                if (opt.type === "percent") {
                  calculatedPrice += Math.round((basePrice * Number(opt.priceValue ?? 0)) / 100);
                } else {
                  calculatedPrice += Number(opt.priceValue ?? 0);
                }
              }
            });
          }
        });
        price = Math.max(0, isNaN(calculatedPrice) ? 0 : calculatedPrice);
      } else {
        const factor = questions.reduce((acc, qq) => {
          const v = sell.answers[qq.key] || [];
          if (v.length === 0) return acc * 0.8;
          const avgFactor = v.reduce((sum, val) => {
            const opt = qq.options.find(o => o.value === val);
            return sum + Number(opt?.factor ?? 0.8);
          }, 0) / v.length;
          return acc * (isNaN(avgFactor) ? 0.8 : avgFactor);
        }, 1);
        const base = Number(model?.basePrice || 0);
        const priceMin = model?.priceMin !== undefined ? Number(model.priceMin) : Math.round(base * 0.7);
        const priceMax = model?.priceMax !== undefined ? Number(model.priceMax) : base;
        const calc = Math.round((priceMin + (priceMax - priceMin) * factor) / 100) * 100;
        price = Math.max(0, isNaN(calc) ? base : calc);
      }
      if (isNaN(price) || price === null || price === undefined) {
        price = Number(model?.basePrice || 0);
      }
      console.log("DEBUG QuestionnairePage price calculation:", {
        price,
        algoId: sell.algoId,
        algoBasePrice: sell.algoBasePrice,
        answers: sell.answers,
        answerIndices: sell.answerIndices,
      });
      sellStore.setPrice(price);
      nav("/price");
      submitLead({
        phone: sell.phone,
        deviceName: model.name,
        brandId: sell.brandId,
        productId: sell.productId,
        algoId: sell.algoId,
        basePrice: sell.algoBasePrice || model.basePrice,
        quotedPrice: price,
        answers: sell.answers,
        step: "price",
      }).catch(() => {});
    } else {
      setAnim(true);
      setTimeout(() => { setStep(s => s + 1); setAnim(false); }, 180);
    }
  };
  const goPrev = () => { if (step > 0) { setAnim(true); setTimeout(() => { setStep(s => s - 1); setAnim(false); }, 180); } };

  if (loading) return (
    <div className="page-bg"><Header />
      <div style={{ maxWidth:620,margin:"0 auto",padding:"80px 16px",textAlign:"center" }}>
        <div style={{ fontSize:36,marginBottom:16,animation:"spin 1s linear infinite",display:"inline-block" }}>⚙️</div>
        <div style={{ fontFamily:"var(--font-display)",fontWeight:700,fontSize:18,color:"var(--text)" }}>Loading questions…</div>
        <div style={{ fontSize:13,color:"var(--text2)",marginTop:6 }}>Fetching personalised questions for {model.name}</div>
      </div>
    </div>
  );

  if (!q) return null;

  const handleSelectOption = (qKey: string, oValue: string, questionId?: string, optionIndex?: number) => {
    sellStore.setAnswer(qKey, oValue, questionId, optionIndex);
  };

  return (
    <div className="page-bg">
      <Header />
      <div style={{ maxWidth:620,margin:"0 auto",padding:"32px 16px 110px" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
          <Link to={`/brand/${sell.brandId}`} style={{ fontSize:13,color:"var(--text2)",textDecoration:"none" }}>← Change device</Link>
          <span style={{ fontSize:13,color:"var(--text3)",fontWeight:500 }}>{step + 1} / {questions.length}</span>
        </div>

        <div style={{ height:4,background:"var(--border)",borderRadius:99,marginBottom:8,overflow:"hidden" }}>
          <div style={{ height:"100%",borderRadius:99,background:"linear-gradient(90deg,#4f46e5,#818cf8)",transition:"width 0.45s cubic-bezier(0.4,0,0.2,1)",width:`${progress}%` }} />
        </div>
        <div style={{ display:"flex",gap:5,justifyContent:"center",marginBottom:28 }}>
          {questions.map((_,i) => <div key={i} style={{ width:i===step?20:6,height:6,borderRadius:99,background:i<=step?"var(--indigo)":"var(--border)",transition:"all 0.28s" }} />)}
        </div>

        <div style={{ background:"var(--surface)",border:"1.5px solid var(--border)",borderRadius:22,padding:"28px 24px",boxShadow:"var(--shadow-lg)",opacity:anim?0:1,transform:anim?"translateY(7px)":"none",transition:"opacity 0.17s,transform 0.17s",position:"relative",zIndex:1 }}>
          <div style={{ marginBottom:24 }}>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11,fontWeight:700,letterSpacing:"0.07em",color:"var(--brand)",textTransform:"uppercase",marginBottom:2 }}>{model.name}</div>
              <h2 style={{ fontFamily:"var(--font-display)",fontWeight:800,fontSize:"clamp(18px,3vw,22px)",color:"var(--text)",letterSpacing:"-0.3px" }}>{q.title}</h2>
            </div>
            <p style={{ fontSize:13,color:"var(--text2)",marginBottom:20 }}>{q.subtitle}</p>
          </div>

          <div style={{ display:"flex",flexDirection:"column",gap:9 }}>
            {q.options.map(o => {
              const active = selected.includes(o.value);
              return (
                <button
                  type="button"
                  key={o.value}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelectOption(q.key, o.value, o.questionId, o.optionIndex);
                  }}
                  onTouchEnd={(e) => {
                    // Fallback for touch devices where click events might be delayed or swallowed
                    e.preventDefault();
                    handleSelectOption(q.key, o.value, o.questionId, o.optionIndex);
                  }}
                  style={{
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"space-between",
                    borderRadius:12,
                    border:active?"2px solid var(--indigo)":"1.5px solid var(--border)",
                    background:active?"var(--indigo-light)":"var(--bg)",
                    padding:"14px 16px",
                    cursor:"pointer",
                    textAlign:"left",
                    transition:"all 0.15s",
                    width:"100%",
                    boxShadow:active?"0 0 0 3px rgba(79,70,229,0.1)":"none",
                    WebkitTapHighlightColor:"transparent",
                    touchAction:"manipulation",
                    userSelect:"none",
                    WebkitUserSelect:"none",
                    position:"relative",
                    zIndex:2
                  }}>
                  <div style={{ flex:1,minWidth:0,pointerEvents:"none" }}>
                    <div style={{ fontWeight:600,fontSize:14,color:"var(--text)" }}>{o.label}</div>
                  </div>
                  <div style={{ width:20,height:20,borderRadius:4,flexShrink:0,border:active?"none":"2px solid var(--border)",background:active?"var(--indigo)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s",marginLeft:8,pointerEvents:"none" }}>
                    {active && <span style={{ color:"#fff",fontSize:11,fontWeight:700 }}>✓</span>}
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ display:"flex",gap:9,marginTop:22 }}>
            <button type="button" onClick={goPrev} disabled={step===0} style={{ flex:1,borderRadius:11,border:"1.5px solid var(--border)",background:"var(--bg)",padding:"12px",fontFamily:"var(--font-body)",fontSize:14,fontWeight:500,color:step===0?"var(--text3)":"var(--text)",cursor:step===0?"not-allowed":"pointer",transition:"all 0.18s" }}>← Prev</button>
            <button type="button" onClick={goNext} disabled={selected.length === 0} style={{ flex:2,borderRadius:11,border:"none",background:selected.length > 0?"var(--indigo)":"var(--surface2)",padding:"12px",fontFamily:"var(--font-body)",fontSize:14,fontWeight:600,color:selected.length > 0?"#fff":"var(--text3)",cursor:selected.length > 0?"pointer":"not-allowed",transition:"all 0.22s",boxShadow:selected.length > 0?"0 4px 14px rgba(79,70,229,0.3)":"none" }}>
              {step===questions.length-1?"Get My Price →":"Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
